/**
 * 告警引擎。
 *
 * 这一层只回答一个问题：**现在有什么是操作员必须立刻知道的**。
 * 它的价值不在于"能报出来"，而在于"报得准"——一个天天喊狼来了的地面站会被无视，
 * 那时它报的每一条都不再有用。因此整个实现围绕三件事：
 *
 * 1. **阈值方向单调性（死区）**。任何数值告警的"触发线"与"解除线"必须是**两个不同的值**，
 *    且解除线永远在"安全"的一侧（见 {@link defineThreshold}）。
 *    用同一个阈值时，数值在边界来回抖动会把告警闪成一片、日志刷满，
 *    真正的危险信号被淹没 —— 这是航电告警设计里最基本的一条。
 *
 * 2. **帧驻留（dwell）**。单个超限样本不算数，要连续 N 个新样本**或**持续 T 毫秒
 *    （见 {@link defineDwell}）。单帧毛刺（GPS 跳一下、母线电压瞬降）不应该产生告警，
 *    而持续异常必须报出来 —— 两个方向都有测试钉住。
 *
 * 3. **绝不静默丢弃**。被去抖、驻留、同一 key 重复、等级过低而没进列表的情况，
 *    全部有计数可查（见 {@link AlertCounters}）。"什么都没发生"和"发生了但被吞掉"
 *    在排障时是天壤之别。
 *
 * 另外两条工程约定：
 *
 * - **告警在状态更新/文本到达的同一次调用栈里同步发出**。异步派发会让界面先看到新数值、
 *   过一帧才看到告警，而在"电量 5%"这种场景里，那一帧就是"看漏"。
 * - **引擎不自己跑定时器**。`tick()` 由应用驱动（界面刷新循环里顺便调用即可），
 *   这样它可以被放进 Web Worker、可以被单测精确控制时间。
 *   注意"数据过期"必须靠 `tick()` 才会被发现：数据停了就不会再有状态事件了。
 */

import { classifyPointInPolygon, type LatLon } from '@hgc/ardupilot';
import {
  decodePayload,
  enumByName,
  enumValue,
  messageById,
  messageIdOf,
  type DecodedValues,
  type MavlinkFrame,
} from '@hgc/mavlink';

import { createEventBus, type EventBus, type Unsubscribe } from './events';
import {
  TELEMETRY_STALE_MS,
  isTelemetryStale,
  telemetryAgeMs,
  type StatustextEntry,
  type TelemetryKey,
  type Vehicle,
  type VehicleState,
  type VibrationSample,
} from './vehicle';

/* -------------------------------------------------------------------------- *
 * 1. 告警等级
 * -------------------------------------------------------------------------- */

/**
 * 告警等级。
 *
 * 刻意与 `MAV_SEVERITY` 的八级不同：`MAV_SEVERITY` 是**消息来源**的自述（飞控说这条文本多重要），
 * 而这里是**地面站**对"操作员要做什么"的分级。两者混用会让"飞控发了一条 DEBUG"变成
 * 一条必须处理的告警。映射关系见 {@link severityToLevel}。
 */
export type AlertLevel = 'emergency' | 'critical' | 'warning' | 'caution' | 'info' | 'debug';

/** 等级强度：数字越大越严重。用于升级/降级比较与列表排序。 */
export const ALERT_LEVEL_RANK: Readonly<Record<AlertLevel, number>> = Object.freeze({
  emergency: 5,
  critical: 4,
  warning: 3,
  caution: 2,
  info: 1,
  debug: 0,
});

/** 中文等级名。界面直接显示，不必自己再写一份映射（写两份必然会漂移）。 */
export const ALERT_LEVEL_LABELS: Readonly<Record<AlertLevel, string>> = Object.freeze({
  emergency: '紧急',
  critical: '严重',
  warning: '警告',
  caution: '注意',
  info: '提示',
  debug: '调试',
});

export function alertLevelRank(level: AlertLevel): number {
  return ALERT_LEVEL_RANK[level];
}

/**
 * 是不是"必须被看到"的等级。
 *
 * `emergency` / `critical` 的告警界面**不允许**折叠进"更多"里 —— 一条"飞行终止"
 * 藏在折叠区里等于没有告警。降级/升级都会发事件，界面据此把它推到最显眼处。
 */
export function requiresAttention(level: AlertLevel): boolean {
  return ALERT_LEVEL_RANK[level] >= ALERT_LEVEL_RANK.critical;
}

/** `MAV_SEVERITY` 枚举条目名 → 告警等级。按**名字**判断，不认数字也不是手抄的常量表。 */
function levelOfSeverityName(name: string): AlertLevel {
  const prefix = 'MAV_SEVERITY_';
  const suffix = name.startsWith(prefix) ? name.slice(prefix.length) : name;
  switch (suffix) {
    case 'EMERGENCY':
    case 'ALERT':
      return 'emergency';
    case 'CRITICAL':
      return 'critical';
    case 'ERROR':
      return 'warning';
    case 'WARNING':
      return 'caution';
    case 'NOTICE':
    case 'INFO':
      return 'info';
    case 'DEBUG':
      return 'debug';
    default:
      // 上游加了新等级时宁可显示得保守一点：当"提示"处理，也不能吞掉整条消息
      return 'info';
  }
}

let severityLevels: Map<number, AlertLevel> | null = null;

/**
 * `MAV_SEVERITY` 数值 → 告警等级。
 *
 * 查不到（协议表与固件不同步）时返回 `'info'`：一条内容不明的状态文本，
 * 丢掉比看错更危险。
 */
export function severityToLevel(severity: number): AlertLevel {
  if (severityLevels === null) {
    const map = new Map<number, AlertLevel>();
    for (const entry of enumByName('MAV_SEVERITY')?.entries ?? []) {
      map.set(entry.value, levelOfSeverityName(entry.name));
    }
    severityLevels = map;
  }
  return severityLevels.get(severity) ?? 'info';
}

/* -------------------------------------------------------------------------- *
 * 2. 阈值：方向 + 死区
 * -------------------------------------------------------------------------- */

/**
 * 阈值的方向语义。
 *
 * - `'low'`：**低于**阈值才危险（电量、电压、卫星数、电压余量）。
 * - `'high'`：**高于**阈值才危险（振动、电流、EKF 方差）。
 *
 * 方向不是一个"注释上的约定"，它决定了触发线与解除线的**排列**：
 * `'low'` 的解除线必须在触发线**上方**，`'high'` 的解除线必须在**下方**。
 * 两个数之间那条带子就是死区。
 */
export type ThresholdDirection = 'low' | 'high';

export interface ThresholdPair<D extends ThresholdDirection = ThresholdDirection> {
  readonly direction: D;
  /** 越过它进入告警（`'low'`：`值 <= triggerAt`；`'high'`：`值 >= triggerAt`） */
  readonly triggerAt: number;
  /** 越过它离开告警（`'low'`：`值 >= clearAt`；`'high'`：`值 <= clearAt`） */
  readonly clearAt: number;
}

/**
 * 编译期检查：触发阈值与解除阈值**必须不同**。
 *
 * 相等时给参数类型塞一个必需属性，字面量配置对象没有它 → 编译失败。
 * 这条检查必须存在，是因为"顺手把两个数写成同一个"这件事在代码评审里几乎看不出来，
 * 而它的后果（告警疯狂开关）要等到飞行中数值恰好在阈值附近时才暴露。
 * 把"靠人记得"换成"写错就编译不过"，是唯一能让它在几百个配置里都不失效的办法。
 */
type DistinctThresholds<P> = P extends {
  readonly triggerAt: infer T extends number;
  readonly clearAt: infer C extends number;
}
  ? // 类型已经宽化成 `number`（配置来自 JSON / 参数表）时静态检查无从下手，
    // 这一档交给运行期的 assertValidThreshold；只有字面量才在编译期拦。
    number extends T
    ? unknown
    : number extends C
      ? unknown
      : T extends C
        ? { readonly __triggerMustDifferFromClear: never }
        : unknown
  : unknown;

function assertValidThreshold(
  direction: ThresholdDirection,
  triggerAt: number,
  clearAt: number,
): void {
  if (!Number.isFinite(triggerAt) || !Number.isFinite(clearAt)) {
    throw new Error(`告警阈值必须是有限数：触发 ${triggerAt}、解除 ${clearAt}`);
  }
  if (triggerAt === clearAt) {
    throw new Error(
      `触发阈值与解除阈值不能相同（都是 ${triggerAt}）：同一个阈值会让数值在边界来回抖动时把告警闪成一片`,
    );
  }
  if (direction === 'low' && clearAt <= triggerAt) {
    throw new Error(
      `"低于则危险"的阈值必须在**上方**解除：触发 ${triggerAt}、解除 ${clearAt}（解除阈值必须大于触发阈值）`,
    );
  }
  if (direction === 'high' && clearAt >= triggerAt) {
    throw new Error(
      `"高于则危险"的阈值必须在**下方**解除：触发 ${triggerAt}、解除 ${clearAt}（解除阈值必须小于触发阈值）`,
    );
  }
}

/**
 * 定义一个带死区的阈值对。
 *
 * 这是**唯一**的构造入口（返回的对象是冻结的，且类型上不可变），
 * 所以"触发与解除用同一个值"这件事在类型与运行时都不可能出现。
 * 运行期再查一遍不是多余的：配置可能来自 JSON、参数表或用户设置，那时类型已经丢了。
 */
export function defineThreshold<
  const P extends {
    readonly direction: ThresholdDirection;
    readonly triggerAt: number;
    readonly clearAt: number;
  },
>(spec: P & DistinctThresholds<P>): ThresholdPair<P['direction']> {
  assertValidThreshold(spec.direction, spec.triggerAt, spec.clearAt);
  return Object.freeze({
    direction: spec.direction,
    triggerAt: spec.triggerAt,
    clearAt: spec.clearAt,
  });
}

/** 采样相对某个阈值对落在哪一侧。 */
export type GateSide = 'trigger' | 'clear' | 'deadband' | 'unavailable';

/**
 * 把一个采样归类到阈值对的哪一侧。
 *
 * 判据**只看数值落在哪个区间**，与当前是否已触发无关 —— 状态记忆留给 {@link createLatch}。
 * 分开的理由：这样"死区内波动不产生任何翻转"就可以被单独断言，
 * 而不是混在状态机里靠结果反推。
 */
export function classifyAgainstThreshold(
  pair: ThresholdPair,
  value: number | null,
): GateSide {
  if (value === null || !Number.isFinite(value)) return 'unavailable';
  if (pair.direction === 'low') {
    if (value <= pair.triggerAt) return 'trigger';
    if (value >= pair.clearAt) return 'clear';
    return 'deadband';
  }
  if (value >= pair.triggerAt) return 'trigger';
  if (value <= pair.clearAt) return 'clear';
  return 'deadband';
}

/** 阈值的中文说明。界面把这个字符串直接显示出来，用户才知道告警为什么在这个数上响。 */
export function describeThreshold(pair: ThresholdPair, unit: string): string {
  const trigger = formatMeasured(pair.triggerAt, unit);
  const clear = formatMeasured(pair.clearAt, unit);
  return pair.direction === 'low'
    ? `低于 ${trigger} 触发、回升到 ${clear} 以上才解除`
    : `高于 ${trigger} 触发、降到 ${clear} 以下才解除`;
}

/* -------------------------------------------------------------------------- *
 * 3. 驻留（dwell）
 * -------------------------------------------------------------------------- */

/**
 * 驻留要求：连续的**新样本**数，或持续时长（毫秒）。
 *
 * 两个条件是"或"：高频消息靠样本数（10Hz 的 3 帧只有 300ms），
 * 低频消息靠时长（0.5Hz 的电池 3 帧要 6 秒，等不起）。
 *
 * `samples` 数的是**该子系统真的收到了新数据**（用 `VehicleState.updatedAt` 的变化判断），
 * 不是"定时器又转了一圈"。否则界面刷新得越快，驻留就越容易被凑满，
 * 一个后台循环的频率会悄悄改变告警的灵敏度。
 *
 * `ms` 为 0 表示只看样本数。
 */
export interface DwellConfig {
  readonly samples: number;
  readonly ms: number;
}

export interface DwellPair {
  readonly trigger: DwellConfig;
  readonly clear: DwellConfig;
}

function assertDwellConfig(config: DwellConfig, which: string): void {
  if (!Number.isInteger(config.samples) || config.samples < 1) {
    throw new Error(`${which}驻留的样本数必须是 >= 1 的整数，收到 ${config.samples}`);
  }
  if (!Number.isFinite(config.ms) || config.ms < 0) {
    throw new Error(`${which}驻留的时长必须 >= 0，收到 ${config.ms}`);
  }
}

/**
 * 定义触发/解除的驻留要求。
 *
 * 唯一的硬约束是"解除不能比触发更迟钝"：那样告警会粘住不走 ——
 * 信号已经恢复，界面还在报警，操作员会去关掉告警，然后连真的也一起关掉。
 * 反过来（解除更快）是常态：确认危险要谨慎，确认恢复正常可以果断。
 */
export function defineDwell(trigger: DwellConfig, clear: DwellConfig): DwellPair {
  assertDwellConfig(trigger, '触发');
  assertDwellConfig(clear, '解除');
  if (clear.samples > trigger.samples || clear.ms > trigger.ms) {
    throw new Error(
      `解除驻留不能比触发驻留更迟钝：触发 ${trigger.samples} 样本/${trigger.ms}ms、解除 ${clear.samples} 样本/${clear.ms}ms`,
    );
  }
  return Object.freeze({
    trigger: Object.freeze({ samples: trigger.samples, ms: trigger.ms }),
    clear: Object.freeze({ samples: clear.samples, ms: clear.ms }),
  });
}

/* -------------------------------------------------------------------------- *
 * 4. 锁存器：死区 + 驻留 + 状态记忆
 * -------------------------------------------------------------------------- */

export type LatchChange = 'raised' | 'cleared' | 'none';

export interface LatchOutcome {
  readonly change: LatchChange;
  /** 条件已经满足，但驻留还没达标 —— 这是"被抑制"，必须能被统计到 */
  readonly suppressed: boolean;
}

export interface Latch {
  readonly active: boolean;
  /** 当前 pending 队列里的连续新样本数（调试与测试用） */
  readonly pendingSamples: number;
  /**
   * @param breaching `true` 满足触发条件、`false` 满足解除条件、`null` 落在死区或数据不可用
   * @param sample 本次是不是一个**新样本**（新数据到达），false 表示只是又过了一小段时间
   */
  update(breaching: boolean | null, now: number, sample: boolean): LatchOutcome;
  /** 清空状态记忆（换围栏、重新挂载车辆时用） */
  reset(): void;
}

const LATCH_IDLE: LatchOutcome = Object.freeze({ change: 'none', suppressed: false });
const LATCH_SUPPRESSED: LatchOutcome = Object.freeze({ change: 'none', suppressed: true });
const LATCH_RAISED: LatchOutcome = Object.freeze({ change: 'raised', suppressed: false });
const LATCH_CLEARED: LatchOutcome = Object.freeze({ change: 'cleared', suppressed: false });

/**
 * 一个"死区 + 驻留 + 记忆"的锁存器。
 *
 * 关键行为：`breaching === null`（死区内波动，或这一路数据暂时读不到）**什么都不做** ——
 * 既不清 pending，也不改状态。这是刻意的：
 * - 死区内来回穿越不该产生任何状态翻转；
 * - 数据读不到时也不该顺手把告警解掉。「丢了一帧」不等于「危险过去了」，
 *   这一路的真实情况由"数据过期"告警去说。
 */
export function createLatch(trigger: DwellConfig, clear: DwellConfig, initial = false): Latch {
  let active = initial;
  let pendingSince: number | null = null;
  let pendingSamples = 0;

  const resetPending = (): void => {
    pendingSince = null;
    pendingSamples = 0;
  };

  const dwellSatisfied = (config: DwellConfig, now: number): boolean => {
    if (pendingSamples >= config.samples) return true;
    if (config.ms > 0 && pendingSince !== null && now - pendingSince >= config.ms) return true;
    return false;
  };

  const advance = (now: number, sample: boolean): void => {
    if (pendingSamples === 0) pendingSince = now;
    if (sample) pendingSamples += 1;
  };

  return {
    get active() {
      return active;
    },
    get pendingSamples() {
      return pendingSamples;
    },
    reset() {
      active = initial;
      resetPending();
    },
    update(breaching, now, sample) {
      if (breaching === null) return LATCH_IDLE;

      if (breaching) {
        if (active) {
          resetPending();
          return LATCH_IDLE;
        }
        advance(now, sample);
        if (dwellSatisfied(trigger, now)) {
          active = true;
          resetPending();
          return LATCH_RAISED;
        }
        return LATCH_SUPPRESSED;
      }

      if (!active) {
        resetPending();
        return LATCH_IDLE;
      }
      advance(now, sample);
      if (dwellSatisfied(clear, now)) {
        active = false;
        resetPending();
        return LATCH_CLEARED;
      }
      return LATCH_SUPPRESSED;
    },
  };
}

/* -------------------------------------------------------------------------- *
 * 5. 数值格式化
 * -------------------------------------------------------------------------- */

/** 单位与数字之间要不要空格：`%`（1 个字符）不空，`m/s/s` 空。 */
function formatMeasured(value: number, unit: string | null): string {
  const text = Number.isInteger(value) ? String(value) : value.toFixed(1);
  if (unit === null || unit === '') return text;
  return unit.length <= 1 ? `${text}${unit}` : `${text} ${unit}`;
}

/* -------------------------------------------------------------------------- *
 * 6. PreArm：把"为什么不能解锁"解析成结构化列表
 * -------------------------------------------------------------------------- */

export type PreArmParseResult =
  | { readonly kind: 'blocker'; readonly reason: string }
  | { readonly kind: 'passed' }
  | null;

/**
 * 解析 `PreArm: xxx` / `Arm: xxx`。
 *
 * 真实 ArduPilot 在解锁被拒时会连续播报多条（`AP_Arming` 每隔约 1 秒把**所有**未通过项
 * 各发一条），而且同一项的文案会随数值变化。因此这里只负责把"一条文本"变成
 * "一个阻塞项/一次通过"，去重与聚合交给告警引擎（key 里带原因文本）。
 *
 * 两个容易踩的坑：
 * - 前缀必须带冒号。否则 `Arming motors`（解锁成功的正常播报）会被解析成阻塞项 "ing motors"，
 *   界面就会在飞机正常起飞时显示一条"不能解锁"。测试里钉住了这一点。
 * - "通过"的判定要同时接受中英文：固件语言包不同，同一件事可能是
 *   `PreArm: 检查通过` 或 `PreArm checks passed`。
 */
export function parsePreArmText(text: string): PreArmParseResult {
  // `\b` 是这里的要点：`(prearm|arm)\b` 才能让 `Arming motors` / `Armed` 这类
  // 正常播报不匹配（它们后面紧跟的还是单词字符），而 `Arm: xxx`、`PreArm checks passed`
  // 能匹配。冒号本身可有可无 —— 固件语言包不同，写法不止一种。
  const match = /^\s*(prearm|arm)\b\s*:?\s*(.*)$/i.exec(text);
  if (match === null) return null;
  const reason = (match[2] ?? '').trim();
  if (reason === '') return null;
  if (isPreArmPassed(reason)) return { kind: 'passed' };
  return { kind: 'blocker', reason };
}

function isPreArmPassed(reason: string): boolean {
  // 先看否定写法：`PreArm: 罗盘未通过` 里也含"通过"，
  // 只看肯定词会把"没通过"读成"通过了"，那比不解析更糟。
  if (/不通过|未通过|没通过|失败|fail/i.test(reason)) return false;
  return /通过|passed|\bpass\b|checks? ok|prearm ok/i.test(reason);
}

/* -------------------------------------------------------------------------- *
 * 7. 传感器健康（SYS_STATUS 位域）
 * -------------------------------------------------------------------------- */

export interface SensorHealthBit {
  /** 枚举条目名（`MAV_SYS_STATUS_SENSOR_*`）。**告警 key 用它而不是中文名**：中文会改，枚举名跟着上游走 */
  readonly name: string;
  readonly mask: number;
  /** 中文名；协议表里没有对照时退回枚举名 */
  readonly label: string;
  readonly present: boolean;
  readonly enabled: boolean;
  /** `true` = 健康。**health 位为 0 表示不健康**（这个极性最容易写反，测试钉住） */
  readonly healthy: boolean;
  /** 该不该向用户报告：存在 + 已启用 + 不健康 */
  readonly shouldReport: boolean;
}

const SENSOR_LABELS: ReadonlyMap<string, string> = new Map<string, string>([
  ['MAV_SYS_STATUS_SENSOR_3D_GYRO', '陀螺仪'],
  ['MAV_SYS_STATUS_SENSOR_3D_ACCEL', '加速度计'],
  ['MAV_SYS_STATUS_SENSOR_3D_MAG', '磁力计'],
  ['MAV_SYS_STATUS_SENSOR_ABSOLUTE_PRESSURE', '气压计'],
  ['MAV_SYS_STATUS_SENSOR_DIFFERENTIAL_PRESSURE', '空速计'],
  ['MAV_SYS_STATUS_SENSOR_GPS', 'GPS'],
  ['MAV_SYS_STATUS_SENSOR_OPTICAL_FLOW', '光流'],
  ['MAV_SYS_STATUS_SENSOR_VISION_POSITION', '视觉定位'],
  ['MAV_SYS_STATUS_SENSOR_LASER_POSITION', '激光测距'],
  ['MAV_SYS_STATUS_SENSOR_EXTERNAL_GROUND_TRUTH', '外部真值'],
  ['MAV_SYS_STATUS_SENSOR_ANGULAR_RATE_CONTROL', '角速率控制'],
  ['MAV_SYS_STATUS_SENSOR_ATTITUDE_STABILIZATION', '姿态稳定'],
  ['MAV_SYS_STATUS_SENSOR_YAW_POSITION', '航向控制'],
  ['MAV_SYS_STATUS_SENSOR_Z_ALTITUDE_CONTROL', '高度控制'],
  ['MAV_SYS_STATUS_SENSOR_XY_POSITION_CONTROL', '水平位置控制'],
  ['MAV_SYS_STATUS_SENSOR_MOTOR_OUTPUTS', '电机输出'],
  ['MAV_SYS_STATUS_SENSOR_RC_RECEIVER', '遥控接收机'],
  ['MAV_SYS_STATUS_SENSOR_3D_GYRO2', '陀螺仪 2'],
  ['MAV_SYS_STATUS_SENSOR_3D_ACCEL2', '加速度计 2'],
  ['MAV_SYS_STATUS_SENSOR_3D_MAG2', '磁力计 2'],
  ['MAV_SYS_STATUS_SENSOR_BATTERY', '电池'],
  ['MAV_SYS_STATUS_SENSOR_PROXIMITY', '避障雷达'],
  ['MAV_SYS_STATUS_SENSOR_SATCOM', '卫星通信'],
  ['MAV_SYS_STATUS_SENSOR_PROPULSION', '动力系统'],
  ['MAV_SYS_STATUS_GEOFENCE', '围栏'],
  ['MAV_SYS_STATUS_AHRS', '姿态估计'],
  ['MAV_SYS_STATUS_TERRAIN', '地形数据'],
  ['MAV_SYS_STATUS_REVERSE_MOTOR', '反向电机'],
  ['MAV_SYS_STATUS_LOGGING', '日志记录'],
  ['MAV_SYS_STATUS_PREARM_CHECK', '解锁前检查'],
  ['MAV_SYS_STATUS_OBSTACLE_AVOIDANCE', '避障'],
  ['MAV_SYS_STATUS_RECOVERY_SYSTEM', '回收系统'],
  ['MAV_SYS_STATUS_SENSOR_LEAK', '漏水检测'],
  ['MAV_SYS_STATUS_SENSOR_3D_GYRO3', '陀螺仪 3'],
  ['MAV_SYS_STATUS_SENSOR_3D_ACCEL3', '加速度计 3'],
  ['MAV_SYS_STATUS_SENSOR_3D_GYRO4', '陀螺仪 4'],
  ['MAV_SYS_STATUS_SENSOR_3D_ACCEL4', '加速度计 4'],
  ['MAV_SYS_STATUS_SENSOR_3D_MAG3', '磁力计 3'],
  ['MAV_SYS_STATUS_SENSOR_3D_MAG4', '磁力计 4'],
]);

/**
 * 不该当成"传感器"上报的位。
 *
 * `MAV_SYS_STATUS_EXTENSION_USED` 只是"扩展位域有效"的标志位，它本身不是设备，
 * 报"扩展位域异常"纯属噪声。
 */
const SENSOR_BITS_IGNORED: ReadonlySet<string> = new Set(['MAV_SYS_STATUS_EXTENSION_USED']);

let sensorBitCache: Map<string, readonly { name: string; mask: number; label: string }[]> | null =
  null;

/** 把位域枚举表编译成"一条一个位"的清单（懒建 + 缓存） */
function sensorBitsOf(enumName: string): readonly { name: string; mask: number; label: string }[] {
  sensorBitCache ??= new Map();
  const cached = sensorBitCache.get(enumName);
  if (cached !== undefined) return cached;
  const bits: { name: string; mask: number; label: string }[] = [];
  for (const entry of enumByName(enumName)?.entries ?? []) {
    if (entry.value <= 0 || !Number.isInteger(entry.value)) continue;
    // 只认 2 的幂：协议表里出现非位掩码条目时直接跳过，而不是算出一个错位的映射
    if ((entry.value & (entry.value - 1)) !== 0) continue;
    if (SENSOR_BITS_IGNORED.has(entry.name)) continue;
    bits.push({
      name: entry.name,
      mask: entry.value,
      label: SENSOR_LABELS.get(entry.name) ?? entry.name,
    });
  }
  // 位值从小到大：界面按"主传感器 → 备份传感器"的顺序展示更自然
  bits.sort((a, b) => a.mask - b.mask);
  const frozen = Object.freeze(bits.map((bit) => Object.freeze(bit)));
  sensorBitCache.set(enumName, frozen);
  return frozen;
}

/**
 * 把 `SYS_STATUS` 的三个位域展开成"哪个传感器 + 中文名 + 正常/异常"。
 *
 * **极性**：`health` 位为 **1 表示健康**，为 0 表示不健康（这里写反一次，
 * 界面上就会出现"所有传感器都坏了"或者"什么都正常"）。测试专门钉住了它。
 *
 * 是否上报看 `present && enabled`：没装的传感器（位为 0）健康位也必然是 0，
 * 只按 health 判断的话，一台没接 RC 的多旋翼会一直挂着"遥控接收机异常"。
 *
 * 位运算用 `(value & mask) !== 0` 而不是 `(value & mask) === mask` 之类的写法：
 * `present` 是 uint32，最高位在 JS 位运算里会变成负数，`!== 0` 是唯一对所有位都成立的判据。
 */
export function describeSensorHealth(
  present: number,
  enabled: number,
  health: number,
  enumName = 'MAV_SYS_STATUS_SENSOR',
): readonly SensorHealthBit[] {
  return sensorBitsOf(enumName).map((bit) => {
    const isPresent = (present & bit.mask) !== 0;
    const isEnabled = (enabled & bit.mask) !== 0;
    const isHealthy = (health & bit.mask) !== 0;
    return {
      name: bit.name,
      mask: bit.mask,
      label: bit.label,
      present: isPresent,
      enabled: isEnabled,
      healthy: isHealthy,
      shouldReport: isPresent && isEnabled && !isHealthy,
    };
  });
}

/* -------------------------------------------------------------------------- *
 * 8. 规则：默认阈值与依据
 * -------------------------------------------------------------------------- */

export type NumericMetric =
  | 'battery-remaining'
  | 'vibration-level'
  | 'vibration-clipping'
  | 'ekf-variance';

export type BooleanMetric = 'ekf-failure' | 'ekf-converging' | 'sensor-health' | 'fence';

export type AlertMetric = NumericMetric | BooleanMetric;

export type AlertSource =
  | 'battery'
  | 'vibration'
  | 'ekf'
  | 'fence'
  | 'sensor-health'
  | 'telemetry'
  | 'statustext'
  | 'prearm'
  | 'system';

interface AlertRuleBase {
  /** 稳定 id：锁存器按它索引，覆盖配置也按它匹配 */
  readonly id: string;
  /** 告警 key。**多个档位共用同一个 key**，这样"电量偏低"升级成"电量严重不足"是同一条告警。 */
  readonly key: string;
  readonly source: AlertSource;
  readonly level: AlertLevel;
  readonly title: string;
  /** 触发依据（中文）：为什么用这个数。会出现在告警详情里，用户看得见判据 */
  readonly rationale: string;
  readonly dwell: DwellPair;
}

export interface NumericAlertRule extends AlertRuleBase {
  readonly metric: NumericMetric;
  readonly unit: string;
  readonly threshold: ThresholdPair;
}

export interface BooleanAlertRule extends AlertRuleBase {
  readonly metric: BooleanMetric;
}

export type AlertRule = NumericAlertRule | BooleanAlertRule;

/**
 * 内置规则与依据。
 *
 * 每个数值都写了出处，而不是"随手取的整数"：
 * - 电量 20% / 10%：ArduPilot 的 `BATT_LOW_VOLT` / `BATT_CRT_VOLT` 出厂值折算到
 *   常见 4S 锂电（16.8V 满、13.2V 空）大约就是 20% / 10%，与 Mission Planner 的显示口径一致。
 * - 振动 30 / 60 m/s/s：ArduPilot 的 VIBE 经验量级（30 以下正常，60 以上会劣化姿态估计）。
 * - EKF 速度方差 1.0：ArduPilot 在 0.8~1.0 附近开始把估计器判为不可信。
 * - 驻留样本数/时长：按该消息的名义频率取，保证"连续"这件事在真实链路上有意义（见各条注释）。
 */
export const DEFAULT_RULES: readonly AlertRule[] = Object.freeze([
  {
    id: 'battery-low',
    key: 'battery',
    source: 'battery',
    metric: 'battery-remaining',
    level: 'warning',
    title: '电量偏低',
    unit: '%',
    threshold: defineThreshold({ direction: 'low', triggerAt: 20, clearAt: 25 }),
    // 电池名义 0.5Hz：3 个样本要 6 秒，所以真正生效的是 2 秒的时长条件。
    // 2 秒足够吃掉"起飞瞬间电压塌陷"这种瞬时压降，又不至于让低电量告警迟到。
    dwell: defineDwell({ samples: 3, ms: 2000 }, { samples: 2, ms: 1000 }),
    rationale: '低于 20% 触发、回升到 25% 以上才解除：留出返航余量，5 个百分点的死区吃掉悬停时的瞬时压降',
  },
  {
    id: 'battery-critical',
    key: 'battery',
    source: 'battery',
    metric: 'battery-remaining',
    level: 'critical',
    title: '电量严重不足',
    unit: '%',
    threshold: defineThreshold({ direction: 'low', triggerAt: 10, clearAt: 15 }),
    dwell: defineDwell({ samples: 3, ms: 2000 }, { samples: 2, ms: 1000 }),
    rationale: '低于 10% 触发、回升到 15% 才解除：此时必须就地降落，已经等不及返航',
  },
  {
    id: 'vibration-high',
    key: 'vibration',
    source: 'vibration',
    metric: 'vibration-level',
    level: 'caution',
    title: '振动偏大',
    unit: 'm/s/s',
    threshold: defineThreshold({ direction: 'high', triggerAt: 30, clearAt: 25 }),
    // 振动是高频信号，单帧尖峰太常见（磕一下、硬着陆）。0.5 秒 / 2 个样本只用来滤掉孤立的尖峰。
    dwell: defineDwell({ samples: 3, ms: 1000 }, { samples: 2, ms: 500 }),
    rationale: 'ArduPilot 的 VIBE 经验量级：高于 30 m/s/s 值得注意，低于 25 才认为已经下去',
  },
  {
    id: 'vibration-severe',
    key: 'vibration',
    source: 'vibration',
    metric: 'vibration-level',
    level: 'warning',
    title: '振动严重',
    unit: 'm/s/s',
    threshold: defineThreshold({ direction: 'high', triggerAt: 60, clearAt: 50 }),
    dwell: defineDwell({ samples: 3, ms: 1000 }, { samples: 2, ms: 500 }),
    rationale: '高于 60 m/s/s 时加速度计会把振动当成真实加速度，姿态与高度估计会明显劣化',
  },
  {
    id: 'vibration-clipping',
    key: 'vibration-clipping',
    source: 'vibration',
    metric: 'vibration-clipping',
    level: 'caution',
    title: '加速度计已打到量程',
    unit: '次',
    threshold: defineThreshold({ direction: 'high', triggerAt: 1, clearAt: 0 }),
    // 计数是**上电后累计**的，所以这条告警一旦出现就不会自己消失（只有重启才清零）。
    // 这是刻意的：裁剪表示"已经丢过数据"，不是趋势预警。
    dwell: defineDwell({ samples: 2, ms: 1000 }, { samples: 1, ms: 500 }),
    rationale: '裁剪计数 > 0 表示振动已经把加速度计推出量程，那一段时间的姿态估计是错的',
  },
  {
    id: 'ekf-failure',
    key: 'ekf-failure',
    source: 'ekf',
    metric: 'ekf-failure',
    level: 'critical',
    title: 'EKF 故障',
    // EKF_STATUS_REPORT 名义 1Hz：2 个样本 ≈ 2 秒。不给更长是为了不被"状态位抖一下"骗到，
    // 也不给更短 —— 一次 EKF 状态位异常就报"故障"会让人去修一台好飞机。
    dwell: defineDwell({ samples: 2, ms: 1500 }, { samples: 1, ms: 800 }),
    rationale: '状态位显示"已初始化，但姿态/位置/速度里缺了一项，或 GPS 定位在跳变"',
  },
  {
    id: 'ekf-converging',
    key: 'ekf-converging',
    source: 'ekf',
    metric: 'ekf-converging',
    level: 'info',
    title: 'EKF 正在收敛',
    // 2 个样本（约 2 秒）才提示：刚上电的那一两拍本来就在收敛，立刻提示没有信息量。
    dwell: defineDwell({ samples: 2, ms: 0 }, { samples: 1, ms: 0 }),
    rationale: '`EKF_UNINITIALIZED`：开机没拿到定位、估计器还没收敛。**这是正常的**，所以是"提示"而不是"故障"',
  },
  {
    id: 'ekf-variance',
    key: 'ekf-variance',
    source: 'ekf',
    metric: 'ekf-variance',
    level: 'warning',
    title: 'EKF 方差过大',
    unit: '(m/s)²',
    threshold: defineThreshold({ direction: 'high', triggerAt: 1, clearAt: 0.8 }),
    // 方差本身是统计量，天然会来回抖：3 个样本 / 2 秒，避免统计噪声直接变成告警。
    dwell: defineDwell({ samples: 3, ms: 2000 }, { samples: 2, ms: 1000 }),
    rationale: '速度/水平位置方差高于 1.0 时导航已经不可信；0.2 的死区吸收统计抖动',
  },
  {
    id: 'sensor-health',
    key: 'sensor-health',
    source: 'sensor-health',
    metric: 'sensor-health',
    level: 'warning',
    title: '传感器异常',
    // 飞控自检期间健康位偶尔闪一下（1Hz），3 个样本 / 3 秒的要求把这类闪烁挡在告警之外。
    dwell: defineDwell({ samples: 3, ms: 3000 }, { samples: 2, ms: 1000 }),
    rationale: 'SYS_STATUS 健康位为 0（**位为 1 才是健康**），且该传感器存在并已启用',
  },
  {
    id: 'fence-breach',
    key: 'fence-breach',
    source: 'fence',
    metric: 'fence',
    level: 'critical',
    title: '越出围栏',
    // 位置 2Hz：2 个样本 / 1 秒。围栏判定本身已经带 5 米边界容差（见 fenceEdgeToleranceM），
    // 驻留是第二道闸：单次 GPS 跳点不该让飞机"闯进"报警状态。
    dwell: defineDwell({ samples: 2, ms: 1000 }, { samples: 1, ms: 500 }),
    rationale: '机体位置落在已加载围栏之外（边界 5 米容差内仍算围栏内）',
  },
]);

/* -------------------------------------------------------------------------- *
 * 9. 告警数据与事件
 * -------------------------------------------------------------------------- */

export type AlertStatus = 'active' | 'cleared';

/**
 * 一条告警。不可变：每次变化都换一个新对象（订阅者可以直接按引用比较）。
 *
 * `status` 解除后**不删除**：界面需要显示"刚刚恢复"，而"恢复了"和"从来没报过"
 * 完全是两件事。历史按 `historyLimit` 保留。
 */
export interface Alert {
  /** 稳定标识：同一问题的重复出现只会更新这一条，绝不新增 */
  readonly key: string;
  readonly source: AlertSource;
  readonly level: AlertLevel;
  /** 中文标题（界面主行） */
  readonly title: string;
  /** 中文详情：触发依据 + 当前实测值 */
  readonly detail: string;
  /** 最近一次观察到的数值（阈值类告警）；文本/状态类为 null */
  readonly value: number | null;
  readonly unit: string | null;
  /** 应急/严重：界面**不允许**折叠这一条 */
  readonly mustSee: boolean;
  readonly status: AlertStatus;
  /** 第一次触发（不清除；即使中途解除过也保留最初时间） */
  readonly firstAt: number;
  readonly updatedAt: number;
  readonly clearedAt: number | null;
  /** 触发次数：从"不存在/已解除"变成 active 的次数。持续存在的异常只算一次。 */
  readonly triggers: number;
}

export type AlertEventReason =
  | 'first'
  | 'reactivated'
  | 'escalated'
  | 'deescalated'
  | 'value-changed'
  | 'cleared';

export interface AlertEvent {
  readonly alert: Alert;
  readonly reason: AlertEventReason;
}

export interface AlertLevelChangeEvent {
  readonly key: string;
  readonly alert: Alert;
  readonly from: AlertLevel;
  readonly to: AlertLevel;
}

export type AlertEngineEvents = {
  /** 新告警出现（key 第一次进入列表），或已解除的告警再次出现 */
  raised: AlertEvent;
  /** 已存在的告警内容变了（数值、等级、文案） */
  updated: AlertEvent;
  /** 等级上升。界面据此把它推到最显眼的位置 */
  escalated: AlertLevelChangeEvent;
  /** 等级下降（例如电量从 8% 回到 12%，从"严重"降回"警告"） */
  deescalated: AlertLevelChangeEvent;
  /** 解除：**不删除**，界面显示"刚恢复" */
  cleared: AlertEvent;
};

/**
 * 抑制计数器。
 *
 * 存在的理由只有一条：**"什么都没发生"和"发生了但被吞掉"必须能区分开**。
 * 告警不响时，排障的人第一件事就是问"是被去抖挡了，还是真的没超限"。
 */
export interface AlertCounters {
  /** 真正进入过 active 的告警条数 */
  readonly raised: number;
  /** 超限了但驻留没达标（含解除侧的驻留） */
  readonly dwellSuppressed: number;
  /** 采样落在死区内的次数（死区吸收了多少次边界抖动） */
  readonly deadbandHeld: number;
  /** 同一 key 重复出现、内容没变：只刷新时间，没有新增条目 */
  readonly duplicate: number;
  /** 等级低于 `minLevel` 被丢弃（例如 DEBUG 级状态文本） */
  readonly belowMinLevel: number;
  /** 该子系统从未收到过数据，因此不判"过期" */
  readonly neverSeen: number;
  /** 数值不可用（没数据、哨兵值），无法判断 */
  readonly unavailable: number;
  /** 链路已断（心跳过期）时被抑制的其它子系统"过期"告警：根因已经报过了 */
  readonly mutedWhileLinkDown: number;
  /** 历史超限被淘汰的条数 */
  readonly historyTrimmed: number;
  /** SYS_STATUS 载荷解码失败的帧数 */
  readonly decodeFailures: number;
}

type MutableCounters = { -readonly [K in keyof AlertCounters]: number };

function createCounters(): MutableCounters {
  return {
    raised: 0,
    dwellSuppressed: 0,
    deadbandHeld: 0,
    duplicate: 0,
    belowMinLevel: 0,
    neverSeen: 0,
    unavailable: 0,
    mutedWhileLinkDown: 0,
    historyTrimmed: 0,
    decodeFailures: 0,
  };
}

/* -------------------------------------------------------------------------- *
 * 10. 子系统中文名与过期等级
 * -------------------------------------------------------------------------- */

/** 各子系统的中文名，用于"XX 数据已过期"这类文案与界面标题 */
export const TELEMETRY_LABELS: Readonly<Record<TelemetryKey, string>> = Object.freeze({
  heartbeat: '心跳',
  attitude: '姿态',
  position: '位置',
  gps: 'GPS',
  battery: '电池',
  ekf: '估计器（EKF）',
  vibration: '振动',
  rc: '遥控',
  hud: '飞行数据',
  home: '返航点',
  mission: '航线进度',
  statustext: '状态文本',
});

/**
 * "过期"这种告警本身的等级。
 *
 * 心跳过期 = 断链：界面上**所有**数值都已经是上一次的读数，必须先说这件事，所以是"严重"。
 * 姿态/位置过期会让姿态仪与地图静止在一个错误的状态上，是"警告"。
 * 其余子系统缺数据只是"少看一块"，是"注意"。
 */
export const TELEMETRY_STALE_LEVEL: Readonly<Record<TelemetryKey, AlertLevel>> = Object.freeze({
  heartbeat: 'critical',
  attitude: 'warning',
  position: 'warning',
  gps: 'caution',
  battery: 'caution',
  ekf: 'warning',
  vibration: 'caution',
  rc: 'caution',
  hud: 'caution',
  home: 'caution',
  mission: 'caution',
  statustext: 'info',
});

/**
 * 参与"数据过期"判断的子系统。
 *
 * 排除的是**事件驱动**的三路：返航点、航线进度、状态文本。它们本来就没有周期消息，
 * "好久没更新"是正常状态而不是故障（返航点可能整场飞行只有一次）。按周期消息的
 * 口径去要求它们，只会得到永远亮着的假告警。
 */
const STALE_MONITORED: readonly TelemetryKey[] = Object.freeze([
  'heartbeat',
  'attitude',
  'position',
  'gps',
  'battery',
  'ekf',
  'vibration',
  'rc',
  'hud',
]);

/* -------------------------------------------------------------------------- *
 * 11. 引擎
 * -------------------------------------------------------------------------- */

/**
 * 状态文本告警的存活时间（毫秒）。
 *
 * 状态文本是**事件**而不是"状态"：它没有"解除"的那一刻。如果不设上限，一条
 * 三个月前的警告会永远占着告警列表，列表就退化成日志的副本（那界面里真正要看的
 * 东西全被挤走了）。60 秒的依据：ArduPilot 对持续存在的问题大约每秒重播一次
 * （`AP_Arming` 的重播间隔是 1 秒级），60 秒内一条都没再来，几乎不可能是"还在持续"。
 * 原文一条不丢地留在"最近状态文本"面板里，所以这条上限不会让信息消失。
 */
export const DEFAULT_STATUSTEXT_TTL_MS = 60_000;

export interface AlertEngineOptions {
  readonly vehicle: Vehicle;
  /** 时间源。默认 `Date.now`；测试注入假时钟后整条链路都是确定的 */
  readonly now?: () => number;
  /**
   * 低于这个等级的告警直接丢弃，默认 `'caution'`。
   *
   * 默认值不是 `'info'` 是刻意的：ArduPilot 的普通播报（`Armed`、`Flight mode: GUIDED`）
   * 都在 `MAV_SEVERITY_INFO` 上，把它们全变成告警会让列表变成状态文本面板的副本，
   * 真正的告警被淹掉。界面另有"最近状态文本"面板显示全部原文。
   * 被丢掉的每一条都会计入 `belowMinLevel`，所以不是"静默"的。
   */
  readonly minLevel?: AlertLevel;
  /** 保留的历史条数（默认 200，只淘汰已解除的，活跃告警永不淘汰） */
  readonly historyLimit?: number;
  /**
   * 围栏判定的边界容差（米，默认 5）。
   *
   * GPS 水平误差典型 1~3 米，贴着围栏飞的点每秒都可能翻转"内/外"结论。
   * 5 米既是死区（容差内一律算"还在围栏里"，不触发也不解除），
   * 又不会让越界告警迟到太多（5 m/s 巡航时是 1 秒）。
   */
  readonly fenceEdgeToleranceM?: number;
  /** 状态文本告警的存活时间（默认 {@link DEFAULT_STATUSTEXT_TTL_MS}） */
  readonly statustextTtlMs?: number;
  /** 覆盖内置规则。给了就整体替换（便于测试与厂家定制），不再合并 */
  readonly rules?: readonly AlertRule[];
  readonly onListenerError?: (error: unknown, type: string) => void;
}

interface AlertDraft {
  readonly key: string;
  readonly source: AlertSource;
  readonly level: AlertLevel;
  readonly title: string;
  readonly detail: string;
  readonly value: number | null;
  readonly unit: string | null;
}

type EkfStatus = 'unknown' | 'converging' | 'failed' | 'healthy';

/** 从状态位判断 EKF 处于哪种情形（"没收敛"与"故障"必须分开，否则开机会吓人一跳） */
function ekfStatusOf(state: VehicleState): EkfStatus {
  const flags = state.ekfFlags;
  if (flags === null) return 'unknown';
  // 0 位全空 = 连姿态估计都没有：还没开始收敛
  if (flags === 0) return 'converging';
  const uninitialized = enumValue('EKF_STATUS_FLAGS', 'EKF_UNINITIALIZED') ?? 0;
  if (uninitialized !== 0 && (flags & uninitialized) !== 0) return 'converging';
  return state.ekfHealthy === true ? 'healthy' : 'failed';
}

/** 振动的"大小"：三个轴取绝对值最大的那个（最坏的一根轴决定结论） */
function vibrationMagnitude(vibration: VibrationSample | null): number | null {
  if (vibration === null) return null;
  let worst: number | null = null;
  for (const axis of [vibration.x, vibration.y, vibration.z]) {
    if (axis === null || !Number.isFinite(axis)) continue;
    const magnitude = Math.abs(axis);
    if (worst === null || magnitude > worst) worst = magnitude;
  }
  return worst;
}

function numericValueOf(metric: NumericMetric, state: VehicleState): number | null {
  switch (metric) {
    case 'battery-remaining':
      return state.remainingPct;
    case 'vibration-level':
      return vibrationMagnitude(state.vibration);
    case 'vibration-clipping':
      return state.vibration === null ? null : state.vibration.clipping;
    case 'ekf-variance': {
      // 速度方差与水平位置方差取大者：两者都指向同一件事（估计器不自信），
      // 界面只想看到最坏的那一个，而不是两条重复的告警。
      const values = [state.ekfVelocityVariance, state.ekfPosHorizVariance].filter(
        (value): value is number => value !== null && Number.isFinite(value),
      );
      return values.length === 0 ? null : Math.max(...values);
    }
  }
}

function telemetryKeyOf(metric: NumericMetric): TelemetryKey {
  switch (metric) {
    case 'battery-remaining':
      return 'battery';
    case 'vibration-level':
    case 'vibration-clipping':
      return 'vibration';
    case 'ekf-variance':
      return 'ekf';
  }
}

/** 数值告警的详情：依据 + 当前实测值 + 阈值口径。用户要能看到"为什么在这个数上响"。 */
function numericDetail(rule: NumericAlertRule, value: number | null): string {
  const measured = value === null ? '当前无数据' : `当前 ${formatMeasured(value, rule.unit)}`;
  return `${measured}；${describeThreshold(rule.threshold, rule.unit)}（${rule.rationale}）`;
}

/** `MAV_STATE` → 等级。只有真正需要操作员介入的状态才进告警列表。 */
const SYSTEM_STATE_LEVELS: ReadonlyMap<string, AlertLevel> = new Map<string, AlertLevel>([
  ['MAV_STATE_EMERGENCY', 'emergency'],
  ['MAV_STATE_CRITICAL', 'critical'],
  ['MAV_STATE_FLIGHT_TERMINATION', 'emergency'],
  ['MAV_STATE_POWEROFF', 'caution'],
]);

/** 为什么这条要单独报：`system_status` 是飞控自己的总判断，与逐项遥测是两回事 */
const SYSTEM_STATE_LEVELS_HINT =
  '这是飞控自报的总体状态，比逐项遥测更权威 —— 它意味着机载软件自己认为有严重问题';

const SYSTEM_STATE_TITLES: ReadonlyMap<string, string> = new Map<string, string>([
  ['MAV_STATE_EMERGENCY', '飞控自报"紧急"状态'],
  ['MAV_STATE_CRITICAL', '飞控自报"严重"状态'],
  ['MAV_STATE_FLIGHT_TERMINATION', '飞控已触发飞行终止'],
  ['MAV_STATE_POWEROFF', '飞控正在关机'],
]);

let systemStateNames: Map<number, string> | null = null;

function systemStateName(value: number): string | null {
  if (systemStateNames === null) {
    systemStateNames = new Map<number, string>();
    for (const entry of enumByName('MAV_STATE')?.entries ?? []) {
      systemStateNames.set(entry.value, entry.name);
    }
  }
  return systemStateNames.get(value) ?? null;
}

function numberField(values: DecodedValues, name: string): number | null {
  const value = values[name];
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'bigint') {
    const converted = Number(value);
    return Number.isSafeInteger(converted) ? converted : null;
  }
  return null;
}

/**
 * 告警引擎：一辆车一个实例。
 *
 * 一辆车一个实例（而不是一个引擎看多辆车）是刻意的：key 只有在"同一辆车"的范围内
 * 才稳定（`1:1` 的"电量偏低"和 `2:1` 的"电量偏低"是两个不同的东西）。
 * 界面跟随 `VehicleRegistry.primaryChanged` 切换实例即可。
 */
export class AlertEngine {
  readonly vehicle: Vehicle;
  readonly events: EventBus<AlertEngineEvents>;
  readonly rules: readonly AlertRule[];

  private readonly now: () => number;
  private readonly minLevel: AlertLevel;
  private readonly historyLimit: number;
  private readonly fenceEdgeToleranceM: number;
  private readonly statustextTtlMs: number;

  /** 当前所有告警（活跃 + 已解除的历史），插入顺序为历史顺序 */
  private readonly alerts = new Map<string, Alert>();
  /** key → (ruleId → 该规则当前贡献的内容)。同一 key 下的多个档位都记在这里，用来算最高等级 */
  private readonly drafts = new Map<string, Map<string, AlertDraft>>();
  private readonly latches = new Map<string, Latch>();
  private readonly sensorLatches = new Map<string, Latch>();
  /** 各子系统最后一次计入"新样本"的时间戳（来自 `VehicleState.updatedAt`） */
  private readonly sampleAt = new Map<TelemetryKey, number>();
  private readonly neverSeen = new Set<TelemetryKey>();
  /** 本段断链中已经计过数的"被抑制的过期告警" */
  private readonly mutedStaleKeys = new Set<TelemetryKey>();
  /** 状态文本告警 key → 最后一次出现的时间（超过 TTL 就自动解除） */
  private readonly statustextSeenAt = new Map<string, number>();
  /** 当前处于"读数不可用"状态的数据源；用来把 unavailable 计数限制成"每次进入"一次 */
  private readonly unavailableKeys = new Set<string>();
  private readonly counters: MutableCounters = createCounters();
  private readonly unsubscribes: Unsubscribe[] = [];

  private fence: readonly LatLon[] | null = null;
  private health: readonly SensorHealthBit[] = [];
  private linkDown = false;
  private wasArmed = false;
  private disposed = false;
  private readonly sysStatusId: number | null;

  constructor(options: AlertEngineOptions) {
    this.vehicle = options.vehicle;
    this.now = options.now ?? (() => Date.now());
    this.minLevel = options.minLevel ?? 'caution';
    this.historyLimit = Math.max(1, options.historyLimit ?? 200);
    this.fenceEdgeToleranceM = Math.max(0, options.fenceEdgeToleranceM ?? 5);
    this.statustextTtlMs = Math.max(0, options.statustextTtlMs ?? DEFAULT_STATUSTEXT_TTL_MS);
    this.rules = options.rules ?? DEFAULT_RULES;
    this.sysStatusId = messageIdOf('SYS_STATUS') ?? null;
    this.events = createEventBus<AlertEngineEvents>(
      options.onListenerError === undefined ? {} : { onListenerError: options.onListenerError },
    );

    // 状态变化与新样本同步处理：告警在同一次调用栈里发出去，界面不会先看到新数值、
    // 过一帧才看到告警（"电量 5%"这种场景里，那一帧就是看漏）。
    this.unsubscribes.push(
      this.vehicle.events.on('state', (state) => {
        this.evaluate(state, this.now());
      }),
    );
    this.unsubscribes.push(
      this.vehicle.events.on('statustext', (entry) => {
        this.handleStatustext(entry);
      }),
    );
    // SYS_STATUS 的健康位域**不在 VehicleState 里**（车辆状态只保留了电压/电流/电量），
    // 所以这里自己解一遍载荷。1Hz 的消息，解一次的开销可以忽略。
    this.unsubscribes.push(
      this.vehicle.events.on('frame', (frame) => {
        this.handleFrame(frame);
      }),
    );
  }

  /* --------------------------- 查询 --------------------------- */

  /**
   * 全部告警：活跃的在前（按等级 → 最近更新排序），已解除的在后（按解除时间倒序）。
   * 界面直接渲染这个列表即可，不必自己再排。
   */
  list(): readonly Alert[] {
    return this.sorted();
  }

  active(): readonly Alert[] {
    return this.sorted().filter((alert) => alert.status === 'active');
  }

  get(key: string): Alert | undefined {
    return this.alerts.get(key);
  }

  /** 当前"为什么不能解锁"的阻塞项列表（PreArm 失败），界面用它渲染解锁检查清单 */
  preArmBlockers(): readonly Alert[] {
    return this.sorted().filter(
      (alert) => alert.source === 'prearm' && alert.status === 'active',
    );
  }

  /** 最近一次 `SYS_STATUS` 展开出来的全部传感器状态（含正常项，供界面画健康表） */
  sensorHealth(): readonly SensorHealthBit[] {
    return this.health;
  }

  /** 抑制计数：告警没响时用它回答"是被挡了还是真没超限" */
  stats(): Readonly<AlertCounters> {
    return Object.freeze({ ...this.counters });
  }

  /* --------------------------- 配置 --------------------------- */

  /**
   * 设置本地已加载的围栏（来自航线/围栏上传）。`null` 表示没有围栏。
   *
   * 换围栏会清掉围栏告警并重置锁存器：旧围栏的判定结论对新围栏没有意义，
   * 留着它会出现"换了围栏但告警还挂着"的假象。
   */
  setFence(polygon: readonly LatLon[] | null): void {
    if (this.disposed) return;
    if (polygon === null || polygon.length < 3) {
      this.fence = null;
    } else {
      this.fence = Object.freeze(
        polygon.map((point) => Object.freeze({ latDeg: point.latDeg, lonDeg: point.lonDeg })),
      );
    }
    const fenceRule = this.ruleFor('fence');
    if (fenceRule === null) return;
    this.latchOf(fenceRule.id, fenceRule.dwell).reset();
    // 立刻按新围栏判一次：用户上传完围栏时飞机可能已经在界外，
    // 等到下一个位置帧才报（2Hz 也要半秒）没有意义。
    this.evaluateFence(this.vehicle.state, this.now(), new Map());
  }

  /* --------------------------- 驱动 --------------------------- */

  /**
   * 推进一次评估。应用应当在自己的时钟上调用它（界面的刷新循环里顺手调一次即可），
   * 频率 1~10Hz 都合适。
   *
   * **必须调用**：数据一旦停止，就不会再有状态事件，"数据过期"这类告警只能靠它发现。
   */
  tick(now?: number): void {
    if (this.disposed) return;
    this.evaluate(this.vehicle.state, now ?? this.now());
  }

  /** 销毁：退订车辆的三个事件源并清空自己的总线（`listenerCount()` 归零） */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    for (const off of this.unsubscribes.splice(0)) off();
    this.events.clear();
  }

  get isDisposed(): boolean {
    return this.disposed;
  }

  /* --------------------------- 评估 --------------------------- */

  private evaluate(state: VehicleState, now: number): void {
    if (this.disposed) return;
    const samples = new Map<TelemetryKey, boolean>();
    this.linkDown = this.isLinkDown(state, now);
    if (!this.linkDown) this.mutedStaleKeys.clear();
    for (const rule of this.rules) {
      if ('threshold' in rule) {
        this.evaluateNumeric(rule, state, now, samples);
      } else if (rule.metric !== 'sensor-health') {
        // 传感器健康由 SYS_STATUS 帧驱动（见 handleFrame），不走快照这条路
        this.evaluateBoolean(rule, state, now, samples);
      }
    }
    this.evaluateStaleness(state, now);
    this.expireStatustextAlerts(now);
    this.evaluateSystemState(state, now);
    this.evaluateArming(state, now);
  }

  /**
   * 这一路是不是来了**新样本**。
   *
   * 用 `VehicleState.updatedAt` 的变化来判断，而不是"引擎被调用了几次"：
   * 界面刷新得越快、定时器转得越勤，样本就不该被数得越多。同一次评估里
   * 多个规则看同一路数据时只算一次（用 `cache` 去重）。
   */
  private sampleOf(
    state: VehicleState,
    key: TelemetryKey,
    cache: Map<TelemetryKey, boolean>,
  ): boolean {
    const cached = cache.get(key);
    if (cached !== undefined) return cached;
    const at = state.updatedAt[key];
    if (at === null) {
      cache.set(key, false);
      return false;
    }
    const isNew = this.sampleAt.get(key) !== at;
    if (isNew) this.sampleAt.set(key, at);
    cache.set(key, isNew);
    return isNew;
  }

  /**
   * 记录"这一路读数不可用"。
   *
   * 按 `tick` 计数会把它变成一个没有意义的秒表（1Hz 刷新就是每秒 +1），
   * 所以只在**进入**不可用状态时计一次。
   */
  private noteUnavailable(source: string, unavailable: boolean): void {
    if (!unavailable) {
      this.unavailableKeys.delete(source);
      return;
    }
    if (this.unavailableKeys.has(source)) return;
    this.unavailableKeys.add(source);
    this.counters.unavailable += 1;
  }

  private latchOf(ruleId: string, dwell: DwellPair): Latch {
    let latch = this.latches.get(ruleId);
    if (latch === undefined) {
      latch = createLatch(dwell.trigger, dwell.clear);
      this.latches.set(ruleId, latch);
    }
    return latch;
  }

  private ruleFor(metric: BooleanMetric): BooleanAlertRule | null {
    for (const rule of this.rules) {
      if (!('threshold' in rule) && rule.metric === metric) return rule;
    }
    return null;
  }

  /* --------------------- 数值规则 --------------------- */

  private evaluateNumeric(
    rule: NumericAlertRule,
    state: VehicleState,
    now: number,
    samples: Map<TelemetryKey, boolean>,
  ): void {
    const sample = this.sampleOf(state, telemetryKeyOf(rule.metric), samples);
    const value = numericValueOf(rule.metric, state);
    const side = classifyAgainstThreshold(rule.threshold, value);
    this.noteUnavailable(rule.metric, side === 'unavailable');
    if (sample && side === 'deadband') this.counters.deadbandHeld += 1;
    const breaching = side === 'trigger' ? true : side === 'clear' ? false : null;
    const latch = this.latchOf(rule.id, rule.dwell);
    const outcome = latch.update(breaching, now, sample);
    if (outcome.suppressed) this.counters.dwellSuppressed += 1;
    const draft = latch.active ? draftOfNumeric(rule, value) : null;
    this.publishDraft(rule.key, rule.id, draft, sample, now);
  }

  /* --------------------- 布尔规则 --------------------- */

  private evaluateBoolean(
    rule: BooleanAlertRule,
    state: VehicleState,
    now: number,
    samples: Map<TelemetryKey, boolean>,
  ): void {
    if (rule.metric === 'fence') {
      this.evaluateFence(state, now, samples);
      return;
    }

    const sample = this.sampleOf(state, 'ekf', samples);
    const status = ekfStatusOf(state);
    this.noteUnavailable(rule.metric, status === 'unknown');
    let breaching: boolean | null;
    if (status === 'unknown') {
      breaching = null;
    } else if (rule.metric === 'ekf-failure') {
      breaching = status === 'failed';
    } else {
      breaching = status === 'converging';
    }

    const latch = this.latchOf(rule.id, rule.dwell);
    const outcome = latch.update(breaching, now, sample);
    if (outcome.suppressed) this.counters.dwellSuppressed += 1;
    const draft: AlertDraft | null = latch.active
      ? {
          key: rule.key,
          source: rule.source,
          level: rule.level,
          title: rule.title,
          detail: detailOfBoolean(rule, status),
          value: null,
          unit: null,
        }
      : null;
    this.publishDraft(rule.key, rule.id, draft, sample, now);
  }

  /**
   * 围栏判定。
   *
   * 三态输入：`inside` / `boundary` / `outside`。`boundary`（离边界 5 米以内）
   * **什么都不做** —— 它就是围栏判定自带的死区。只靠驻留挡不住"贴着边界飞"：
   * 每个样本都会真的越界，驻留只会被反复凑满然后清零。
   */
  private evaluateFence(
    state: VehicleState,
    now: number,
    samples: Map<TelemetryKey, boolean>,
  ): void {
    const rule = this.ruleFor('fence');
    if (rule === null) return;
    const latch = this.latchOf(rule.id, rule.dwell);
    if (this.fence === null) {
      // 没配围栏不是"读数不可用"，而是"这个功能没开"：不计数、不判定
      latch.reset();
      this.publishDraft(rule.key, rule.id, null, false, now);
      return;
    }

    const sample = this.sampleOf(state, 'position', samples);
    const lat = state.latDeg;
    const lon = state.lonDeg;
    let breaching: boolean | null;
    let detail = rule.rationale;
    if (lat === null || lon === null) {
      // 配了围栏但读不到位置：判不了。这是"不可用"，必须计数，但不能报越界
      this.noteUnavailable('fence', true);
      breaching = null;
    } else {
      this.noteUnavailable('fence', false);
      const result = classifyPointInPolygon(
        { latDeg: lat, lonDeg: lon },
        this.fence,
        this.fenceEdgeToleranceM,
      );
      if (result === 'boundary') {
        breaching = null;
        if (sample) this.counters.deadbandHeld += 1;
      } else {
        breaching = result === 'outside';
        detail = `机体位置 ${lat.toFixed(6)}, ${lon.toFixed(6)} 落在围栏${result === 'outside' ? '之外' : '之内'}；${rule.rationale}`;
      }
    }

    const outcome = latch.update(breaching, now, sample);
    if (outcome.suppressed) this.counters.dwellSuppressed += 1;
    const draft = latch.active
      ? {
          key: rule.key,
          source: rule.source,
          level: rule.level,
          title: rule.title,
          detail,
          value: null,
          unit: null,
        }
      : null;
    this.publishDraft(rule.key, rule.id, draft, sample, now);
  }

  /* --------------------- 传感器健康（帧驱动） --------------------- */

  private handleFrame(frame: MavlinkFrame): void {
    if (this.disposed) return;
    if (this.sysStatusId === null || frame.messageId !== this.sysStatusId) return;
    const definition = messageById(frame.messageId);
    if (definition === undefined) return;

    let values: DecodedValues;
    try {
      values = decodePayload(definition, frame.payload);
    } catch {
      this.counters.decodeFailures += 1;
      return;
    }

    const present = numberField(values, 'onboard_control_sensors_present') ?? 0;
    const enabled = numberField(values, 'onboard_control_sensors_enabled') ?? 0;
    const health = numberField(values, 'onboard_control_sensors_health') ?? 0;
    const extendedHealth = numberField(values, 'onboard_control_sensors_health_extended') ?? 0;

    const bits = [
      ...describeSensorHealth(present, enabled, health),
      ...describeSensorHealth(
        numberField(values, 'onboard_control_sensors_present_extended') ?? 0,
        numberField(values, 'onboard_control_sensors_enabled_extended') ?? 0,
        extendedHealth,
        'MAV_SYS_STATUS_SENSOR_EXTENDED',
      ),
    ];
    this.health = Object.freeze(bits);

    const rule = this.ruleFor('sensor-health');
    if (rule === null) return;
    for (const bit of bits) {
      const key = `${rule.key}:${bit.name}`;
      const ruleId = key;
      const latch = this.sensorLatch(ruleId, rule.dwell);
      if (!bit.present || !bit.enabled) {
        // 没装/没启用的传感器：健康位对它没有意义，既不报也不留状态
        latch.reset();
        this.publishDraft(key, ruleId, null, false, frame.receivedAt);
        continue;
      }
      // 每收到一帧 SYS_STATUS 就是一次采样：驻留的"连续 N 个样本"在这里才有意义
      const outcome = latch.update(!bit.healthy, frame.receivedAt, true);
      if (outcome.suppressed) this.counters.dwellSuppressed += 1;
      const draft: AlertDraft | null = latch.active
        ? {
            key,
            source: rule.source,
            level: rule.level,
            title: `${bit.label}异常`,
            detail: `${bit.label}在 SYS_STATUS 里被标记为**不健康**（health 位为 0，${
              bit.name
            }）；同一份状态里"存在"与"已启用"都是 1，说明这不是"没装"，而是真的报了故障`,
            value: null,
            unit: null,
          }
        : null;
      this.publishDraft(key, ruleId, draft, true, frame.receivedAt);
    }
  }

  private sensorLatch(ruleId: string, dwell: DwellPair): Latch {
    let latch = this.sensorLatches.get(ruleId);
    if (latch === undefined) {
      latch = createLatch(dwell.trigger, dwell.clear);
      this.sensorLatches.set(ruleId, latch);
    }
    return latch;
  }

  /* --------------------- 数据过期 --------------------- */

  /** 链路是不是已经断了（心跳收到过、但现在超时）。没收到过心跳时返回 false，不能凭猜。 */
  private isLinkDown(state: VehicleState, now: number): boolean {
    if (state.updatedAt.heartbeat === null) return false;
    return isTelemetryStale(state, 'heartbeat', now);
  }

  private evaluateStaleness(state: VehicleState, now: number): void {
    for (const key of STALE_MONITORED) {
      const updatedAt = state.updatedAt[key];
      if (updatedAt === null) {
        // 从未收到过数据 ≠ 数据过期。开机瞬间把所有子系统都判成"过期"，
        // 会刷出十来条告警，把它们后面真正的故障淹掉。
        if (!this.neverSeen.has(key)) {
          this.neverSeen.add(key);
          this.counters.neverSeen += 1;
        }
        this.publishDraft(staleKey(key), staleKey(key), null, false, now);
        continue;
      }

      const stale = isTelemetryStale(state, key, now);
      if (!stale) {
        this.publishDraft(staleKey(key), staleKey(key), null, false, now);
        continue;
      }

      if (this.linkDown && key !== 'heartbeat') {
        // 链路已经断了，界面上的"姿态过期/位置过期/…"全是同一个根因的后果。
        // 只报"失去联系"一条，其余计入 mutedWhileLinkDown —— 抑制，但可查。
        // 每段断链、每个子系统只计一次：按 tick 计数会把它变成一个毫无意义的秒表。
        if (!this.mutedStaleKeys.has(key)) {
          this.mutedStaleKeys.add(key);
          this.counters.mutedWhileLinkDown += 1;
        }
        this.publishDraft(staleKey(key), staleKey(key), null, false, now);
        continue;
      }
      this.mutedStaleKeys.delete(key);

      // 时长按秒取整：否则每 tick 详情里的"已 X.X 秒"都会变一点，
      // 于是每 tick 发一次 updated，界面被无意义地推着重渲染。
      const rawAge = telemetryAgeMs(state, key, now) ?? 0;
      const age = Math.floor(rawAge / 1000) * 1000;
      const maxAge = TELEMETRY_STALE_MS[key];
      this.publishDraft(
        staleKey(key),
        staleKey(key),
        {
          key: staleKey(key),
          source: 'telemetry',
          level: TELEMETRY_STALE_LEVEL[key],
          title: `${TELEMETRY_LABELS[key]}数据已过期`,
          detail:
            key === 'heartbeat'
              ? `已 ${(age / 1000).toFixed(1)} 秒没有收到心跳（阈值 ${(maxAge / 1000).toFixed(
                  1,
                )} 秒）：**链路已断**，界面上所有数值都是上一次收到的旧值`
              : `已 ${(age / 1000).toFixed(1)} 秒没有更新（阈值 ${(maxAge / 1000).toFixed(
                  1,
                )} 秒）：界面上显示的仍然是上一次收到的数值，不能当作当前状态使用`,
          value: age,
          unit: 'ms',
        },
        false,
        now,
      );
    }
  }

  /**
   * 状态文本告警的"自动解除"。
   *
   * 文本是事件，没有"危险过去"的那一刻，所以用"多久没再出现"来近似它。
   * 这一路只能靠 `tick()` 推进 —— 没有新消息就不会有事件，正是本方法要处理的场景。
   */
  private expireStatustextAlerts(now: number): void {
    for (const [key, seenAt] of [...this.statustextSeenAt]) {
      if (now - seenAt < this.statustextTtlMs) continue;
      this.statustextSeenAt.delete(key);
      this.publishDraft(key, key, null, false, now);
    }
  }

  /* --------------------- 飞控自报状态与解锁 --------------------- */

  private evaluateSystemState(state: VehicleState, now: number): void {
    const raw = state.systemStatus;
    const name = raw === null ? null : systemStateName(raw);
    const level = name === null ? undefined : SYSTEM_STATE_LEVELS.get(name);
    const title = name === null ? undefined : SYSTEM_STATE_TITLES.get(name);
    const key = 'system-state';
    if (name === null || level === undefined || title === undefined) {
      this.publishDraft(key, key, null, false, now);
      return;
    }
    // `system_status` 随心跳更新：按"心跳是不是新样本"决定要不要把重复计进统计，
    // 否则每次 tick 都会被算成一次"同一 key 重复出现"。
    const sample = this.sampleOf(state, 'heartbeat', new Map());
    this.publishDraft(
      key,
      key,
      {
        key,
        source: 'system',
        level,
        title,
        detail: `飞控在 HEARTBEAT.system_status 里自报 ${name}；${SYSTEM_STATE_LEVELS_HINT}`,
        value: raw,
        unit: null,
      },
      sample,
      now,
    );
  }

  private evaluateArming(state: VehicleState, now: number): void {
    const armed = state.armed;
    // 解锁成功 = 所有解锁前阻塞项都不成立了。这是权威信号，
    // 比"等固件再播报一条检查通过"可靠（解锁成功本来就不会再播报 PreArm）。
    if (armed && !this.wasArmed) this.clearPreArmBlockers(now);
    this.wasArmed = armed;
  }

  private clearPreArmBlockers(now: number): void {
    for (const [key, byRule] of [...this.drafts]) {
      let removed = false;
      for (const ruleId of [...byRule.keys()]) {
        if (ruleId.startsWith(PREARM_KEY_PREFIX)) {
          byRule.delete(ruleId);
          removed = true;
        }
      }
      if (removed) this.syncKey(key, now, false);
    }
  }

  /* --------------------- STATUSTEXT / PreArm --------------------- */

  private handleStatustext(entry: StatustextEntry): void {
    if (this.disposed) return;
    const now = entry.at;

    // PreArm 先解析、且**不经过 minLevel 过滤**：它承载的是"为什么不能解锁"这个结构信息，
    // 而"检查通过"这类清除信号恰恰是 INFO 级。按等级过滤会把清除信号也一起吞掉，
    // 结果是阻塞项永远挂在界面上。
    const preArm = parsePreArmText(entry.text);
    if (preArm !== null) {
      if (preArm.kind === 'passed') {
        this.clearPreArmBlockers(now);
      } else {
        const level = maxLevel(severityToLevel(entry.severity), 'warning');
        const key = `${PREARM_KEY_PREFIX}${normalizePreArmReason(preArm.reason)}`;
        this.publishDraft(
          key,
          key,
          {
            key,
            source: 'prearm',
            level,
            title: preArm.reason,
            detail:
              '解锁前检查（PreArm）未通过：这一项不解决就无法解锁。' +
              '界面把它列进"为什么不能解锁"，而不是混在状态文本里；' +
              '固件会持续重播未通过项，重播只刷新"最后更新"时间，不会新增条目',
            value: null,
            unit: null,
          },
          true,
          now,
        );
      }
      return;
    }

    const level = severityToLevel(entry.severity);
    if (alertLevelRank(level) < alertLevelRank(this.minLevel)) {
      // 被等级门槛挡下：计数，别让它变成"静默丢弃"
      this.counters.belowMinLevel += 1;
      return;
    }
    const key = `${STATUSTEXT_KEY_PREFIX}${entry.text}`;
    // 记录"最后出现时间"：同一条文本不再出现时，由 expireStatustextAlerts 把它解除
    this.statustextSeenAt.set(key, now);
    const severityName = entry.severityName ?? String(entry.severity);
    this.publishDraft(
      key,
      key,
      {
        key,
        source: 'statustext',
        level,
        title: entry.text,
        detail: `状态文本（${severityName} → ${ALERT_LEVEL_LABELS[level]}）`,
        value: null,
        unit: null,
      },
      true,
      now,
    );
  }

  /* --------------------- 告警发布 --------------------- */

  /**
   * 某条规则当前"贡献"的内容发生变化（或规则不再成立）。
   *
   * 同一个 key 下可以有多条规则（电量 20% 与 10% 是两个档位），所以这里只是把
   * 内容记下来，真正决定"这条告警现在长什么样"的是 {@link syncKey} 里取最高等级那一步。
   */
  private publishDraft(
    alertKey: string,
    ruleId: string,
    draft: AlertDraft | null,
    sample: boolean,
    now: number,
  ): void {
    let byRule = this.drafts.get(alertKey);
    if (byRule === undefined) {
      if (draft === null) return;
      byRule = new Map<string, AlertDraft>();
      this.drafts.set(alertKey, byRule);
    }
    if (draft === null) byRule.delete(ruleId);
    else byRule.set(ruleId, draft);
    this.syncKey(alertKey, now, sample);
  }

  /** 按当前所有已触发档位重算这条告警：新建 / 更新 / 升级 / 降级 / 解除 */
  private syncKey(key: string, now: number, sample: boolean): void {
    const byRule = this.drafts.get(key);
    const previous = this.alerts.get(key);

    if (byRule === undefined || byRule.size === 0) {
      if (byRule !== undefined) this.drafts.delete(key);
      this.clearAlert(key, now);
      return;
    }

    let top: AlertDraft | null = null;
    for (const draft of byRule.values()) {
      if (top === null || alertLevelRank(draft.level) > alertLevelRank(top.level)) top = draft;
    }
    if (top === null) return;

    const alert = this.buildAlert(top, previous, now);
    const unchanged =
      previous !== undefined &&
      previous.status === 'active' &&
      previous.level === alert.level &&
      previous.title === alert.title &&
      previous.detail === alert.detail &&
      previous.value === alert.value &&
      previous.unit === alert.unit;

    this.alerts.set(key, alert);

    if (unchanged) {
      // 纯重复：只刷新时间戳，**不发事件**。1Hz 的遥测每秒发一条"没变化"的更新，
      // 会让界面每秒重渲染一次，而用户看到的东西一个字都没变。
      // 界面要拿刷新后的 `updatedAt`（例如显示"最后更新 12 秒前"），
      // 在自己的 tick 之后重读一次 `list()` 即可 —— 引擎本来就需要被 tick 驱动。
      if (sample) this.counters.duplicate += 1;
      return;
    }

    if (previous === undefined || previous.status === 'cleared') {
      this.counters.raised += 1;
      this.events.emit('raised', {
        alert,
        reason: previous === undefined ? 'first' : 'reactivated',
      });
      this.trimHistory();
      return;
    }

    const from = previous.level;
    const to = alert.level;
    if (alertLevelRank(to) > alertLevelRank(from)) {
      this.events.emit('escalated', { key, alert, from, to });
      this.events.emit('updated', { alert, reason: 'escalated' });
    } else if (alertLevelRank(to) < alertLevelRank(from)) {
      this.events.emit('deescalated', { key, alert, from, to });
      this.events.emit('updated', { alert, reason: 'deescalated' });
    } else {
      this.events.emit('updated', { alert, reason: 'value-changed' });
    }
  }

  private buildAlert(top: AlertDraft, previous: Alert | undefined, now: number): Alert {
    const reactivating = previous === undefined || previous.status === 'cleared';
    return Object.freeze({
      key: top.key,
      source: top.source,
      level: top.level,
      title: top.title,
      detail: top.detail,
      value: top.value,
      unit: top.unit,
      mustSee: requiresAttention(top.level),
      status: 'active',
      firstAt: previous === undefined ? now : previous.firstAt,
      updatedAt: now,
      clearedAt: null,
      triggers: reactivating ? (previous === undefined ? 1 : previous.triggers + 1) : previous.triggers,
    });
  }

  private clearAlert(key: string, now: number): void {
    const previous = this.alerts.get(key);
    if (previous === undefined || previous.status === 'cleared') return;
    const alert: Alert = Object.freeze({
      ...previous,
      status: 'cleared',
      clearedAt: now,
      updatedAt: now,
    });
    this.alerts.set(key, alert);
    this.events.emit('cleared', { alert, reason: 'cleared' });
  }

  /**
   * 保留最近 `historyLimit` 条历史。
   *
   * 只淘汰**已解除**的，且先淘汰非 `mustSee` 的：一条"飞行终止"的历史如果被
   * 十几条普通提示挤出去，事后复盘时最重要的那条就没了。活跃告警永不淘汰。
   */
  private trimHistory(): void {
    while (this.alerts.size > this.historyLimit) {
      let victim: string | null = null;
      let victimClearedAt = Number.POSITIVE_INFINITY;
      let fallback: string | null = null;
      let fallbackClearedAt = Number.POSITIVE_INFINITY;
      for (const [key, alert] of this.alerts) {
        if (alert.status === 'active') continue;
        const clearedAt = alert.clearedAt ?? alert.updatedAt;
        if (alert.mustSee) {
          if (clearedAt < fallbackClearedAt) {
            fallbackClearedAt = clearedAt;
            fallback = key;
          }
          continue;
        }
        if (clearedAt < victimClearedAt) {
          victimClearedAt = clearedAt;
          victim = key;
        }
      }
      const key = victim ?? fallback;
      if (key === null) return;
      this.alerts.delete(key);
      this.drafts.delete(key);
      this.counters.historyTrimmed += 1;
    }
  }

  private sorted(): readonly Alert[] {
    const all = [...this.alerts.values()];
    const active = all
      .filter((alert) => alert.status === 'active')
      .sort(
        (a, b) =>
          alertLevelRank(b.level) - alertLevelRank(a.level) ||
          b.updatedAt - a.updatedAt ||
          a.key.localeCompare(b.key),
      );
    const cleared = all
      .filter((alert) => alert.status === 'cleared')
      .sort((a, b) => (b.clearedAt ?? 0) - (a.clearedAt ?? 0) || a.key.localeCompare(b.key));
    return Object.freeze([...active, ...cleared]);
  }
}

/* -------------------------------------------------------------------------- *
 * 12. 内部辅助
 * -------------------------------------------------------------------------- */

const PREARM_KEY_PREFIX = 'prearm:';
const STATUSTEXT_KEY_PREFIX = 'statustext:';

function staleKey(key: TelemetryKey): string {
  return `stale:${key}`;
}

/** PreArm 原因文本归一化：用来让"同一项"稳定地落到同一个 key 上 */
function normalizePreArmReason(reason: string): string {
  return reason.replace(/\s+/g, ' ').replace(/[。．.]+$/, '').trim();
}

function maxLevel(a: AlertLevel, b: AlertLevel): AlertLevel {
  return alertLevelRank(a) >= alertLevelRank(b) ? a : b;
}

function draftOfNumeric(rule: NumericAlertRule, value: number | null): AlertDraft {
  return {
    key: rule.key,
    source: rule.source,
    level: rule.level,
    title: rule.title,
    detail: numericDetail(rule, value),
    value,
    unit: rule.unit,
  };
}

function detailOfBoolean(rule: BooleanAlertRule, status: EkfStatus): string {
  if (rule.metric === 'ekf-converging') {
    return `EKF 尚未初始化（${status === 'converging' ? 'EKF_UNINITIALIZED 置位' : '状态位全空'}）：${rule.rationale}`;
  }
  return rule.rationale;
}
